import { HttpBackend, HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, enableProdMode, importProvidersFrom, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ActivatedRoute, provideRouter } from '@angular/router';
import { HttpResponseInterceptor } from '@interceptors/HttpResponse.interceptor';
import { TranslateLoader, TranslateModule, TranslateParser } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { EnvironmentService } from '@services/environment.service';
import { AddFiscodOnForeignGuestTransformer } from '@transformers/Requests/AddFiscodOnForeignGuest.transformer';
import { RemoveAutoSSVTransformer } from '@transformers/Requests/RemoveAutoSSV.transformer';
import { RemoveEmptyWebCheckinTransformer } from '@transformers/Requests/RemoveEmptyWebCheckin.transformer';
import { GuestTBATransformer } from '@transformers/Responses/GuestTBA.transformer';
import { RemoveFiscodOnForeignGuestTransformer } from '@transformers/Responses/RemoveFiscodOnForeignGuest.transformer';
import { basePathCurrentEnv } from '@utils/Navigation/basePathCurrentEnv.utils';
import { APP_ROUTES } from 'src/app/Root/app.routes';

import { AppComponent } from './app/app.component';
import { environment } from '@environments/environment';
import { loadLocale , customLocaleFactory } from '@global/config/locales';
import { setupDayjs } from '@global/config/setupDayjs';
import '@global/declarations';
import { NGX_TRANSLATE_DEBUG_CONFIG, NgxTranslateDebugParser } from 'ngx-translate-debug';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { register } from 'swiper/element/bundle';

register();

const displayScaleBreakpoints = [1200, 1400, 1536];

function applyDisplayScaleCompensation(): void {
  const dpr = window.devicePixelRatio || 1;
  const isDpr125 = dpr >= 1.2 && dpr <= 1.35;
  const shouldCompensate = dpr >= 1.45 && dpr <= 1.75;
  const zoom = shouldCompensate ? 0.8 : 1;
  const effectiveLayoutWidth = window.innerWidth / zoom;
  const root = document.documentElement;

  root.classList.toggle('cck-display-scale-dpr-125', isDpr125);
  root.classList.toggle('cck-display-scale-compensated', shouldCompensate);
  root.style.setProperty('--cck-display-scale-zoom', String(zoom));
  root.style.setProperty('--cck-display-scale-dpr', String(dpr));
  root.style.setProperty('--cck-display-scale-layout-width', String(effectiveLayoutWidth));

  displayScaleBreakpoints.forEach((breakpoint) => {
    root.classList.toggle(
      `cck-layout-ge-${breakpoint}`,
      shouldCompensate && effectiveLayoutWidth >= breakpoint
    );
  });
}

applyDisplayScaleCompensation();

if (environment.production) {
  enableProdMode();
  // if (__webpack_public_path__) __webpack_public_path__ = '/Style Library/CCK/';
}

function CustomLoaderFactory(http: HttpBackend, _environmentService: EnvironmentService) {
  const prefix = _environmentService.isLocal()
    ? `https://www.costaextra.it${basePathCurrentEnv(false)}`
    : `${basePathCurrentEnv(false)}`;

  return new TranslateHttpLoader(new HttpClient(http), prefix, '.json');
}

const AppConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(APP_ROUTES),
    provideHttpClient(
      withInterceptors([
        HttpResponseInterceptor,
        RemoveEmptyWebCheckinTransformer,
        RemoveAutoSSVTransformer,
        AddFiscodOnForeignGuestTransformer,
        GuestTBATransformer,
        RemoveFiscodOnForeignGuestTransformer,
      ])
    ),
    {
      provide: LOCALE_ID,
      useValue: customLocaleFactory(window.outputCulture)
    },
    {
      provide: NGX_TRANSLATE_DEBUG_CONFIG,
      useValue: {
        localStorageKey: 'oto-ngx-translate-debug',
      },
    },
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: CustomLoaderFactory,
          deps: [HttpBackend, EnvironmentService, ActivatedRoute],
        },
        parser: {
          provide: TranslateParser,
          useClass: NgxTranslateDebugParser,
        },
        defaultLanguage: 'LabelDispatcher',
      }),
    ),
    provideCharts(withDefaultRegisterables(
      DataLabelsPlugin
    )),
  ]
}

Promise.all([loadLocale(), setupDayjs()]).then(() => {
  bootstrapApplication(AppComponent, {...AppConfig, providers: [provideZoneChangeDetection(), ...AppConfig.providers]}).catch((err) => console.error(err))
})
